import React, { useState } from 'react';
import { formatDateToCompanyFormat } from '../../utils/dateUtils';
import QuotationBillShell from './shared/QuotationBillShell';
import { useQuotationBillData } from './shared/useQuotationBillData';
import QuotationSubPartyBreakdown from './shared/QuotationSubPartyBreakdown';
import QuotationAmountToggle from './shared/QuotationAmountToggle';
import QuotationItemsTable from './shared/QuotationItemsTable';
import QuotationBankTermsFooter from './shared/QuotationBankTermsFooter';

function QuotationTemplate({
  billData = {},
  companyDetails = {},
  partyDetails = {},
  bankDetails: _bankDetails = {},
  showAmountsByDefault = false,
  previewMode = false,
  sharePreview = false,
  pageSize = 'a4',
  orientation = 'portrait',
}) {
  const [showAmounts, setShowAmounts] = useState(showAmountsByDefault);
  const screenPreviewBoost = previewMode || sharePreview;
  const {
    displayItems,
    subPartyBreakdownList,
    totalBeforeDiscount,
    discount,
    payableAmount,
    terms,
    sparseLayout,
    hasLineDiscount,
    discountLabel,
    hasSubPartyColumn,
  } = useQuotationBillData(billData, companyDetails);

  return (
    <QuotationBillShell
      pageSize={pageSize}
      orientation={orientation}
      screenPreviewBoost={screenPreviewBoost}
      sparseLayout={sparseLayout}
    >
      <div className="quotation-doc-header">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
          <div className="flex items-center gap-3 min-w-0">
            {companyDetails.logoUrl && (
              <img src={companyDetails.logoUrl} alt="Logo" className="h-12 w-12 object-contain" />
            )}
            <div>
              <div className="text-xl font-bold uppercase">{companyDetails.firmName || ''}</div>
              {companyDetails.gstin && (
                <div className="text-xs text-gray-600">GSTIN: {companyDetails.gstin}</div>
              )}
              {companyDetails.contactNumber && (
                <div className="text-xs text-gray-600">Contact: {companyDetails.contactNumber}</div>
              )}
            </div>
          </div>
          <div className="text-left sm:text-right w-full sm:w-auto shrink-0">
            <div className="text-2xl font-bold tracking-wider">QUOTATION</div>
            <div className="mt-2">
              <span className="font-semibold">Quotation No:</span>{' '}
              {billData.quotationNumber || billData.invoiceNumber || billData.number || ''}
            </div>
            <div>
              <span className="font-semibold">Date:</span>{' '}
              {formatDateToCompanyFormat(billData.date || billData.invoiceDate, companyDetails)}
            </div>
          </div>
        </div>

        <div className="flex justify-between gap-4 mb-2">
          <div className="flex-1">
            <div className="font-semibold">By:</div>
            <div>{companyDetails.firmName || ''}</div>
            <div>{companyDetails.address || ''}</div>
            <div>
              {companyDetails.city || ''} {companyDetails.pincode || ''}
            </div>
            <div>GSTIN: {companyDetails.gstin || 'N/A'}</div>
            <div>
              Contact: {companyDetails.contactNumber || ''}
              {companyDetails.email ? ` | ${companyDetails.email}` : ''}
            </div>
          </div>
          <div className="flex-1">
            <div className="font-semibold">To:</div>
            <div>{partyDetails.firmName || partyDetails.name || ''}</div>
            <div>{partyDetails.address || ''}</div>
            <div>
              {partyDetails.city || ''} {partyDetails.pincode || ''}
            </div>
            <div>GSTIN: {partyDetails.gstin || 'N/A'}</div>
            <div>
              Contact: {partyDetails.contactNumber || ''}
              {partyDetails.email ? ` | ${partyDetails.email}` : ''}
            </div>
          </div>
        </div>

        {billData.notes && (
          <div className="mb-2">
            <div className="font-semibold">Note:</div>
            <div className="italic text-gray-700">{billData.notes}</div>
          </div>
        )}

        <QuotationSubPartyBreakdown
          breakdown={subPartyBreakdownList}
          billData={billData}
          payableAmount={payableAmount}
          variant="standard"
        />

        <QuotationAmountToggle
          showAmounts={showAmounts}
          onToggle={() => setShowAmounts(!showAmounts)}
          variant="standard"
        />
      </div>

      <QuotationItemsTable
        variant="standard"
        displayItems={displayItems}
        showAmounts={showAmounts}
        totalBeforeDiscount={totalBeforeDiscount}
        discount={discount}
        payableAmount={payableAmount}
        hasLineDiscount={hasLineDiscount}
        discountLabel={discountLabel}
        hasSubPartyColumn={hasSubPartyColumn}
      />

      <QuotationBankTermsFooter companyDetails={companyDetails} terms={terms} variant="standard" />
    </QuotationBillShell>
  );
}

export default QuotationTemplate;
